import { Box, Stack } from '@mui/material';
import { useCallback, useState } from 'react';
import type { GridSortModel } from '@mui/x-data-grid-premium';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { Permission } from '../GeneratedClient';
import { useScreenTitle } from '../Shell/ShellScreenTitle';
import { ResponsivePageHeader } from '../Generic/ResponsivePageHeader';
import { ClientsDataGridV2 } from './ClientsDataGridV2';
import {
  type ClientBrowserRowV2,
  useClientsBrowserViewModel,
} from './useClientsBrowserViewModel';
import { useAppNavigate } from '../Hooks/useAppNavigate';
import { normalizePartneringFamiliesSortMode } from './PartneringFamilies/sortPartneringFamilies';
import {
  clientsSortPresets,
  clientsToolbarSortValue,
  normalizeClientsGridSortModel,
} from './clientsGridSorting';
import {
  FAMILY_MEMBER_CUSTOM_FIELDS_FEATURE_FLAG,
  FUNCTION_ASSIGNMENTS_FEATURE_FLAG,
} from '../featureFlags';
import {
  useAllPartneringFamiliesPermissions,
  useGlobalPermissions,
} from '../Model/SessionModel';
import { wideTablePageSx } from '../Utilities/wideTablePageSx';

const PARTNERING_FAMILIES_SORT_STORAGE_KEY = 'partnering-families-sortMode';

export function ClientsScreenV2() {
  useScreenTitle('Clients');
  const appNavigate = useAppNavigate();
  const globalPermissions = useGlobalPermissions();
  const permissions = useAllPartneringFamiliesPermissions();
  const functionAssignmentsEnabled = useFeatureFlagEnabled(
    FUNCTION_ASSIGNMENTS_FEATURE_FLAG
  );
  const familyMemberCustomFieldsEnabled = useFeatureFlagEnabled(
    FAMILY_MEMBER_CUSTOM_FIELDS_FEATURE_FLAG
  );
  const canViewFunctionAssignments =
    functionAssignmentsEnabled === true &&
    permissions(Permission.ViewV1CaseFunctionAssignments);
  const [sortModel, setSortModel] = useState<GridSortModel>(() => {
    try {
      const stored = window.localStorage.getItem(
        PARTNERING_FAMILIES_SORT_STORAGE_KEY
      );
      return clientsSortPresets[
        normalizePartneringFamiliesSortMode(stored ? JSON.parse(stored) : null)
      ];
    } catch {
      return clientsSortPresets.lastNameAsc;
    }
  });

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    const next = normalizeClientsGridSortModel(model);
    setSortModel(next);
    const preset = clientsToolbarSortValue(next);
    if (preset === 'custom' || preset === 'unsorted') return;
    try {
      window.localStorage.setItem(
        PARTNERING_FAMILIES_SORT_STORAGE_KEY,
        JSON.stringify(preset)
      );
    } catch (error) {
      console.log(error);
    }
  }, []);
  const handleRowClick = useCallback(
    (row: ClientBrowserRowV2) => appNavigate.family(row.familyId),
    [appNavigate]
  );
  const {
    rows,
    counties,
    assignmentRoles,
    familyCustomFields,
    caseCustomFields,
    adultCustomFields,
    childCustomFields,
  } = useClientsBrowserViewModel({ canViewFunctionAssignments });
  const hasFeaturebaseChat = globalPermissions(Permission.AccessSupportScreen);
  return (
    <Box sx={wideTablePageSx(hasFeaturebaseChat)}>
      <Stack spacing={{ xs: 1, sm: 2 }} sx={{ flex: 1, minHeight: 0 }}>
        <ResponsivePageHeader
          title="Clients"
          description="Browse client families, open cases, and arrangement summaries."
        />
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <ClientsDataGridV2
            rows={rows}
            countyOptions={counties}
            assignmentRoles={assignmentRoles}
            familyCustomFields={familyCustomFields}
            caseCustomFields={caseCustomFields}
            adultCustomFields={
              familyMemberCustomFieldsEnabled ? adultCustomFields : []
            }
            childCustomFields={
              familyMemberCustomFieldsEnabled ? childCustomFields : []
            }
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            onRowClick={handleRowClick}
          />
        </Box>
      </Stack>
    </Box>
  );
}
